import { useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../../lib/adminApi";
import AdminModal, { Field, inputCls, ImageField, MultiImageField } from "./AdminModal";

const EMPTY = { name: "", category: "aves", line: "", image: "", extraImages: [], presentation: "", description: "", order: 99 };
const CATEGORY_LABEL = { aves: "Aves", conejos: "Conejos", roedores: "Roedores" };

const AdminProducts = ({ products, reload }) => {
    const [editing, setEditing] = useState(null); // null | {slug?} + fields
    const [saving, setSaving] = useState(false);

    const openNew = () => setEditing({ ...EMPTY });
    const openEdit = (p) => {
        const all = p.images?.length ? p.images : (p.image ? [p.image] : []);
        setEditing({ slug: p.slug, name: p.name, category: p.category, line: p.line || "", image: all[0] || "", extraImages: all.slice(1), presentation: p.presentation || "", description: p.description || "", order: p.order ?? 99 });
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = { name: editing.name, category: editing.category, line: editing.line, image: editing.image, images: [editing.image, ...editing.extraImages].filter(Boolean), presentation: editing.presentation, description: editing.description, order: Number(editing.order) || 99 };
        try {
            if (editing.slug) {
                await adminApi.put(`/admin/products/${editing.slug}`, payload);
                toast.success("Producto actualizado");
            } else {
                await adminApi.post("/admin/products", payload);
                toast.success("Producto creado");
            }
            setEditing(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`¿Eliminar "${p.name}"? Esta acción no se puede deshacer.`)) return;
        try {
            await adminApi.delete(`/admin/products/${p.slug}`);
            toast.success("Producto eliminado");
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="admin-products-panel">
            <div className="flex items-center justify-between">
                <p className="text-sm text-brand-muted">{products.length} productos</p>
                <button
                    data-testid="product-new-button"
                    onClick={openNew}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand-magenta px-6 font-display text-sm font-semibold text-white transition-colors hover:bg-brand-magentadark"
                >
                    <Plus className="h-4 w-4" /> Nuevo producto
                </button>
            </div>
            <ul className="mt-6 space-y-3">
                {products.map((p) => (
                    <li
                        key={p.slug}
                        data-testid={`product-row-${p.slug}`}
                        className="flex items-center gap-4 rounded-2xl border border-black/10 bg-white p-3 shadow-soft"
                    >
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-black/[0.03]">
                            {p.image && <img src={p.image} alt={p.name} className="h-full w-full object-contain" loading="lazy" />}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate font-display font-semibold text-brand-ink">{p.name}</p>
                            <p className="text-xs font-medium uppercase tracking-wider text-brand-muted">
                                {p.line || "—"} · {CATEGORY_LABEL[p.category] || p.category}
                            </p>
                        </div>
                        <button
                            data-testid={`product-edit-${p.slug}`}
                            onClick={() => openEdit(p)}
                            aria-label={`Editar ${p.name}`}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-brand-ink transition-colors hover:bg-black/5"
                        >
                            <Pencil className="h-4 w-4" />
                        </button>
                        <button
                            data-testid={`product-delete-${p.slug}`}
                            onClick={() => remove(p)}
                            aria-label={`Eliminar ${p.name}`}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-brand-magenta transition-colors hover:bg-brand-magenta/10"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </li>
                ))}
            </ul>

            {editing && (
                <AdminModal
                    testId="product-form"
                    title={editing.slug ? "Editar producto" : "Nuevo producto"}
                    onClose={() => setEditing(null)}
                    onSubmit={save}
                    saving={saving}
                >
                    <Field label="Nombre *">
                        <input data-testid="product-form-name" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Categoría *">
                        <select data-testid="product-form-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls}>
                            <option value="aves">Aves</option>
                            <option value="conejos">Conejos</option>
                            <option value="roedores">Roedores</option>
                        </select>
                    </Field>
                    <Field label="Línea (Foody Premium, Smakers, Bloomy...)">
                        <input data-testid="product-form-line" value={editing.line} onChange={(e) => setEditing({ ...editing, line: e.target.value })} className={inputCls} />
                    </Field>
                    <ImageField testId="product-form-image" value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} />
                    <MultiImageField testId="product-form-photos" value={editing.extraImages} onChange={(arr) => setEditing({ ...editing, extraImages: arr })} />
                    <Field label="Presentación (ej. 600 g, 2 unidades, S/M/L)">
                        <input data-testid="product-form-presentation" value={editing.presentation} onChange={(e) => setEditing({ ...editing, presentation: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Descripción del producto (usa ## para títulos y - para listas)">
                        <textarea data-testid="product-form-description" rows={6} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={`${inputCls} py-3`} placeholder="Describe el producto: qué es, beneficios, modo de uso..." />
                    </Field>
                    <Field label="Orden (menor aparece primero)">
                        <input data-testid="product-form-order" type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className={inputCls} />
                    </Field>
                </AdminModal>
            )}
        </div>
    );
};

export default AdminProducts;
